/**
 * ThreadTooltip
 *
 * 개별 스레드 호버 시 표시되는 툴팁
 * - 날짜/시간
 * - 조회수
 * - 가중 참여율
 * - 텍스트 미리보기
 */

import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * 숫자 포맷팅
 */
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

/**
 * 날짜 포맷팅
 */
const formatDate = (timestamp, isKorean) => {
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');

  if (isKorean) {
    return `${month}/${day} ${hours}:${minutes}`;
  }
  return `${month}/${day} ${hours}:${minutes}`;
};

const ThreadTooltip = ({
  thread,
  position,
}) => {
  const { t, i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  if (!thread || !position) return null;

  // 툴팁 위치 계산 (커서 우측 상단에 배치)
  const tooltipStyle = {
    position: 'absolute',
    left: position.x + 16,
    top: position.y - 80,
    transform: 'translateY(0)',
    pointerEvents: 'none',
    zIndex: 100,
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
        transition={{ duration: 0.15 }}
        style={tooltipStyle}
      >
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            minWidth: 160,
            maxWidth: 240,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* 날짜/시간 */}
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
              fontWeight: 600,
              color: 'text.primary',
              display: 'block',
              mb: 1,
            }}
          >
            {formatDate(thread.timestamp, isKorean)}
          </Typography>

          {/* 텍스트 미리보기 */}
          {thread.text && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {thread.text}...
            </Typography>
          )}

          {/* 통계 */}
          <Stack spacing={0.5}>
            {/* 조회수 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('yearEnd.activity.tooltip.views', { defaultValue: '조회수' })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {formatNumber(thread.views)}
              </Typography>
            </Stack>

            {/* 가중 참여율 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('yearEnd.activity.tooltip.engagement', { defaultValue: '참여' })}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {formatNumber(thread.weightedEngagement)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

export default ThreadTooltip;
